import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  GoogleAuthProvider,
  getAuth,
  getRedirectResult,
  onIdTokenChanged,
  sendPasswordResetEmail,
  signInWithCredential,
  signInWithEmailAndPassword,
  signInWithRedirect,
  signOut as fbSignOut,
  type Auth,
  type User,
} from "firebase/auth";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

/**
 * GCP Identity Platform auth (Firebase Auth SDK). Owns password, MFA, and sessions.
 * The portal backend independently verifies the ID token on every request; the
 * client is untrusted. MFA is enforced via Identity Platform config — when a sign-in
 * requires a second factor the SDK throws `auth/multi-factor-auth-required`, which
 * the Login page surfaces to complete the challenge. §4.1/§7
 */

interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
}

function readConfig(): FirebaseConfig | null {
  const apiKey = import.meta.env.VITE_FIREBASE_API_KEY as string | undefined;
  const configuredAuthDomain = import.meta.env.VITE_FIREBASE_AUTH_DOMAIN as string | undefined;
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID as string | undefined;
  if (!apiKey || !configuredAuthDomain || !projectId) return null;
  // The Google OAuth handshake must run first-party: authDomain has to equal the
  // origin actually serving the app (which reverse-proxies /__/auth/* to the
  // Firebase helper — see routes/authProxy.ts). The build-time
  // VITE_FIREBASE_AUTH_DOMAIN only names ONE host, so a second custom domain
  // (e.g. support.pumamesh.com fronting the same Cloud Run service) would send
  // the handshake cross-origin and Chrome's storage partitioning drops the
  // credential ("sign-in loop"/failure). In production, bind authDomain to the
  // live origin so SSO works on every domain that fronts this service; keep the
  // configured value for dev (Vite localhost has no /__/auth/* proxy). Each such
  // domain must also be an Identity Platform authorized domain.
  const authDomain =
    import.meta.env.PROD && typeof window !== "undefined"
      ? window.location.host
      : configuredAuthDomain;
  return { apiKey, authDomain, projectId };
}

interface AuthState {
  ready: boolean;
  user: User | null;
  configured: boolean;
  getToken: () => Promise<string | null>;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

let appSingleton: FirebaseApp | null = null;
let authSingleton: Auth | null = null;

function getFirebaseAuth(): Auth | null {
  const cfg = readConfig();
  if (!cfg) return null;
  if (!appSingleton) appSingleton = initializeApp(cfg);
  if (!authSingleton) authSingleton = getAuth(appSingleton);
  return authSingleton;
}

/**
 * Deterministic completion for the reverse-proxied Google redirect sign-in.
 *
 * On this custom-domain + `/__/auth/*` reverse-proxy setup the Firebase SDK
 * deposits the redirect event in `sessionStorage` on return, but its redirect
 * resolver fails to dispatch that event to the credential exchange (the gapi
 * iframe relay does not survive the proxy), so `getRedirectResult` yields no
 * user and the app bounces back to the login page — the "sign-in loop".
 *
 * The server-side exchange itself is healthy, so when we detect an unconsumed
 * redirect event we finish the handshake ourselves: exchange the returned Google
 * auth code for the Google OAuth id_token via Identity Toolkit, then hand that
 * credential to the SDK via `signInWithCredential` so the session is created and
 * persisted by the SDK exactly as a normal sign-in (onIdTokenChanged fires). §4.1
 */
/**
 * True when we're returning from a Google redirect that still needs finalizing
 * (the SDK left an unconsumed redirect event in sessionStorage). Used to keep the
 * app in its loading state during the async completion so we don't flash the login
 * page for a few seconds before the session lands. §4.1/§8
 */
function hasPendingGoogleRedirect(apiKey: string): boolean {
  try {
    const raw = sessionStorage.getItem(`firebase:redirectEvent:${apiKey}:[DEFAULT]`);
    if (!raw) return false;
    const ev = JSON.parse(raw) as { urlResponse?: string; error?: unknown };
    return Boolean(ev && ev.urlResponse && !ev.error);
  } catch {
    return false;
  }
}

async function completePendingGoogleRedirect(auth: Auth, apiKey: string): Promise<boolean> {
  const eventKey = `firebase:redirectEvent:${apiKey}:[DEFAULT]`;
  const pendingKey = `firebase:pendingRedirect:${apiKey}:[DEFAULT]`;
  const raw = sessionStorage.getItem(eventKey);
  if (!raw) return false;

  type RedirectEvent = { urlResponse?: string; sessionId?: string; error?: unknown };
  let ev: RedirectEvent | null = null;
  try {
    ev = JSON.parse(raw) as RedirectEvent;
  } catch {
    ev = null;
  }
  if (!ev || !ev.urlResponse || ev.error) {
    return false;
  }

  try {
    const resp = await fetch(
      `https://identitytoolkit.googleapis.com/v1/accounts:signInWithIdp?key=${encodeURIComponent(apiKey)}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          requestUri: ev.urlResponse,
          sessionId: ev.sessionId,
          returnSecureToken: true,
          returnIdpCredential: true,
        }),
      },
    );
    const data = (await resp.json()) as {
      oauthIdToken?: string;
      oauthAccessToken?: string;
    };
    if (!resp.ok || !data.oauthIdToken) {
      return false;
    }
    const credential = GoogleAuthProvider.credential(data.oauthIdToken, data.oauthAccessToken);
    await signInWithCredential(auth, credential);
    return true;
  } catch {
    return false;
  } finally {
    // One-shot: the auth code is single-use, so never replay this event.
    sessionStorage.removeItem(eventKey);
    sessionStorage.removeItem(pendingKey);
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const auth = getFirebaseAuth();
  const apiKey = import.meta.env.VITE_FIREBASE_API_KEY as string | undefined;
  const [user, setUser] = useState<User | null>(null);
  // If we're returning from a Google redirect, a completion handshake is about to
  // run. Stay "not ready" so the app shows its loading state instead of briefly
  // flashing the login page while the credential exchange finishes. §4.1/§8
  const completingRef = useRef(Boolean(auth && apiKey && hasPendingGoogleRedirect(apiKey)));
  const [ready, setReady] = useState(!auth && !completingRef.current);

  useEffect(() => {
    if (!auth) return;
    // Complete any pending redirect sign-in. First let the SDK try; if it can't
    // finalize the redirect (the reverse-proxy relay drops the event on this
    // custom domain), fall back to completing the handshake ourselves. Auth state
    // still propagates via onIdTokenChanged. §4.1/§8
    void (async () => {
      let result: unknown = null;
      try {
        result = await getRedirectResult(auth);
      } catch {
        result = null;
      }
      if (!result && apiKey) {
        await completePendingGoogleRedirect(auth, apiKey);
      }
      // The completion attempt is done: on success onIdTokenChanged already
      // delivered the user; on failure the user stays null. Either way stop
      // suppressing readiness so the router can resolve.
      completingRef.current = false;
      setReady(true);
    })();
    return onIdTokenChanged(auth, (u) => {
      setUser(u);
      // While a redirect completion is in-flight, ignore the initial null
      // callback — flipping ready here is what flashed the login page. Once a
      // user arrives (or the completion above resolves) we become ready.
      if (u || !completingRef.current) setReady(true);
    });
  }, [auth, apiKey]);

  const value = useMemo<AuthState>(
    () => ({
      ready,
      user,
      configured: Boolean(auth),
      getToken: async () => (auth?.currentUser ? auth.currentUser.getIdToken() : null),
      signIn: async (email, password) => {
        if (!auth) throw new Error("Auth is not configured.");
        await signInWithEmailAndPassword(auth, email, password);
      },
      signInWithGoogle: async () => {
        if (!auth) throw new Error("Auth is not configured.");
        const provider = new GoogleAuthProvider();
        // Always let the user pick the account (customers and staff share one button).
        provider.setCustomParameters({ prompt: "select_account" });
        // Use a full-page redirect, not a popup. Locked-down/government browsers
        // (and embedded webviews) routinely block popups silently — the click then
        // "does nothing". The Firebase Auth handshake is same-origin here (the app
        // origin proxies /__/auth/* to the Firebase Auth helper and authDomain is set
        // to this origin), so redirect completes first-party without third-party
        // cookies. getRedirectResult() on load finalizes it. §8
        await signInWithRedirect(auth, provider);
      },
      resetPassword: async (email) => {
        if (!auth) throw new Error("Auth is not configured.");
        await sendPasswordResetEmail(auth, email);
      },
      signOut: async () => {
        if (auth) await fbSignOut(auth);
      },
    }),
    [auth, ready, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
